import { useState, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import carouselApi, { CAROUSEL_KEY, UploadCarouselParams } from "../../../api/carouselApi";
import { useGetCarouselHomepage } from "../../../hooks/query/useGetCarouselHomepage";
import { useCreateCarouselHomepage } from "../../../hooks/query/useCreateCarouselHomepage";
import { useUpdateCarouselHomepage } from "../../../hooks/query/useUpdateCarouselHomepage";

interface CarouselItem {
  id: string;
  title: string;
  imageUrl: string;
}

// ─── Icons ────────────────────────────────────────────────────────────────────
const IconPlus = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
    <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
  </svg>
);
const IconEdit = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor">
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
  </svg>
);
const IconTrash = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor">
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
  </svg>
);
const IconX = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);
const IconCheck = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
  </svg>
);
const IconImage = () => (
  <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor" opacity="0.25">
    <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
  </svg>
);
const IconSpinner = () => (
  <svg className="animate-spin" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
  </svg>
);

// ─── Toast ────────────────────────────────────────────────────────────────────
function Toast({ type, msg }: { type: "success" | "error"; msg: string }) {
  return (
    <div className={`fixed bottom-5 right-5 z-[999] flex items-center gap-2 px-4 py-2.5 rounded-xl shadow-2xl text-sm border
      ${type === "success"
        ? "bg-green-950/90 border-green-500/25 text-green-300"
        : "bg-red-950/90 border-red-500/25 text-red-300"}`}>
      {type === "success" ? <IconCheck /> : <IconX />}
      {msg}
    </div>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────
interface ModalProps {
  mode: "add" | "edit";
  initial?: { id: string; title: string };
  onClose: () => void;
  onSubmit: (data: UploadCarouselParams, id?: string) => void;
  isLoading: boolean;
}

function CarouselModal({ mode, initial, onClose, onSubmit, isLoading }: ModalProps) {
  const [title, setTitle] = useState(initial?.title ?? "");
  const [image, setImage] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImage(file);
    setPreview(URL.createObjectURL(file));
  };

  const canSubmit = title.trim() && (mode === "edit" || image);

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({ title: title.trim(), image: image as File }, initial?.id);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl border border-white/10 shadow-2xl" style={{ background: "#13151f" }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h3 className="text-white font-semibold text-sm">
            {mode === "add" ? "Thêm ảnh carousel" : "Chỉnh sửa carousel"}
          </h3>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors">
            <IconX />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Image picker */}
          <div>
            <label className="block text-xs text-gray-400 mb-2">
              Ảnh {mode === "edit" && <span className="text-gray-600">(để trống = giữ ảnh cũ)</span>}
            </label>
            <div
              onClick={() => fileRef.current?.click()}
              className="relative w-full h-44 rounded-xl border-2 border-dashed border-white/10 hover:border-blue-500/40 transition-colors cursor-pointer overflow-hidden flex items-center justify-center"
              style={{ background: "rgba(255,255,255,0.03)" }}
            >
              {preview
                ? <img src={preview} alt="preview" className="w-full h-full object-cover" />
                : <div className="flex flex-col items-center gap-2 text-gray-500">
                  <IconImage />
                  <span className="text-xs">Click để chọn ảnh</span>
                </div>
              }
            </div>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs text-gray-400 mb-2">Tiêu đề</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              placeholder="Nhập tiêu đề..."
              className="w-full h-9 px-3 rounded-lg text-sm text-white placeholder-gray-600 border border-white/10 focus:outline-none focus:border-blue-500/50 transition-colors"
              style={{ background: "rgba(255,255,255,0.06)" }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-white/10">
          <button onClick={onClose} className="px-4 py-1.5 rounded-lg text-sm text-gray-300 border border-white/15 hover:bg-white/10 transition-colors">
            Huỷ
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || isLoading}
            className="px-4 py-1.5 rounded-lg text-sm text-white bg-blue-600 hover:bg-blue-500 disabled:opacity-40 transition-colors flex items-center gap-1.5"
          >
            {isLoading && <IconSpinner />}
            {isLoading ? "Đang lưu..." : mode === "add" ? "Thêm" : "Lưu"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Confirm Delete ───────────────────────────────────────────────────────────
function ConfirmDelete({ name, onConfirm, onCancel, isLoading }: {
  name: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative w-72 rounded-2xl border border-white/10 shadow-2xl p-6 text-center" style={{ background: "#13151f" }}>
        <div className="w-11 h-11 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-3 text-red-400">
          <IconTrash />
        </div>
        <h3 className="text-white font-semibold text-sm mb-1">Xoá ảnh này?</h3>
        <p className="text-gray-500 text-xs mb-5 truncate">"{name}"</p>
        <div className="flex gap-2">
          <button onClick={onCancel} className="flex-1 py-2 rounded-lg text-sm text-gray-300 border border-white/15 hover:bg-white/10 transition-colors">
            Huỷ
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1 py-2 rounded-lg text-sm text-white bg-red-600 hover:bg-red-500 disabled:opacity-40 transition-colors flex items-center justify-center gap-1.5"
          >
            {isLoading && <IconSpinner />}
            Xoá
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Carousel Card ────────────────────────────────────────────────────────────
function CarouselCard({ item, onEdit, onDelete }: {
  item: CarouselItem;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="group relative rounded-xl border border-white/8 overflow-hidden" style={{ background: "rgba(255,255,255,0.04)" }}>
      {/* Image */}
      <div className="relative h-36 overflow-hidden bg-white/5">
        <img
          src={item.imageUrl}
          alt={item.title}
          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
        />
        {/* Action buttons overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-200 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
          <button
            onClick={onEdit}
            className="w-8 h-8 rounded-lg bg-white/20 hover:bg-blue-500 backdrop-blur-sm flex items-center justify-center text-white transition-colors"
            title="Chỉnh sửa"
          >
            <IconEdit />
          </button>
          <button
            onClick={onDelete}
            className="w-8 h-8 rounded-lg bg-white/20 hover:bg-red-500 backdrop-blur-sm flex items-center justify-center text-white transition-colors"
            title="Xoá"
          >
            <IconTrash />
          </button>
        </div>
      </div>
      {/* Title */}
      <div className="px-3 py-2.5">
        <p className="text-white text-xs font-medium truncate">{item.title}</p>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function CarouselEditor() {
  const queryClient = useQueryClient();

  const [modal, setModal] = useState<
    | null
    | { mode: "add" }
    | { mode: "edit"; item: CarouselItem }
  >(null);
  const [deleteTarget, setDeleteTarget] = useState<CarouselItem | null>(null);
  const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);

  const showToast = (type: "success" | "error", msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 2500);
  };

  // Hooks
  const { data, isLoading } = useGetCarouselHomepage();
  const createCarousel = useCreateCarouselHomepage();
  const updateCarousel = useUpdateCarouselHomepage();

  const deleteCarousel = useMutation({
    mutationFn: (id: string) => carouselApi.deleteCarousel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CAROUSEL_KEY });
      setDeleteTarget(null);
      showToast("success", "Đã xoá ảnh");
    },
    onError: () => showToast("error", "Xoá thất bại"),
  });

  const handleSubmit = (formData: UploadCarouselParams, id?: string) => {
    if (id) {
      updateCarousel.mutate(
        { id, data: formData },
        {
          onSuccess: () => { setModal(null); showToast("success", "Cập nhật thành công"); },
          onError: () => showToast("error", "Cập nhật thất bại"),
        }
      );
    } else {
      createCarousel.mutate(formData, {
        onSuccess: () => { setModal(null); showToast("success", "Thêm ảnh thành công"); },
        onError: () => showToast("error", "Thêm ảnh thất bại"),
      });
    }
  };

  const isMutating = createCarousel.isPending || updateCarousel.isPending;
  const items: CarouselItem[] = Array.isArray(data) ? data : [];

  return (
    <>
      {toast && <Toast type={toast.type} msg={toast.msg} />}

      {modal && (
        <CarouselModal
          mode={modal.mode}
          initial={modal.mode === "edit" ? { id: modal.item.id, title: modal.item.title } : undefined}
          onClose={() => setModal(null)}
          onSubmit={handleSubmit}
          isLoading={isMutating}
        />
      )}

      {deleteTarget && (
        <ConfirmDelete
          name={deleteTarget.title}
          onConfirm={() => deleteCarousel.mutate(deleteTarget.id)}
          onCancel={() => setDeleteTarget(null)}
          isLoading={deleteCarousel.isPending}
        />
      )}

      <div className="rounded-xl border border-white/10 overflow-hidden" style={{ background: "rgba(255,255,255,0.04)" }}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10" style={{ background: "rgba(255,255,255,0.03)" }}>
          <span className="text-white text-sm font-medium">
            Danh sách ảnh
            {items.length > 0 && <span className="ml-2 text-xs text-gray-500">({items.length})</span>}
          </span>
          <button
            onClick={() => setModal({ mode: "add" })}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-white bg-blue-600 hover:bg-blue-500 transition-colors"
          >
            <IconPlus />
            Thêm ảnh
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {isLoading ? (
            <div className="flex items-center gap-3 text-gray-400 py-8 justify-center">
              <IconSpinner />
              <span className="text-sm">Đang tải...</span>
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-600">
              <IconImage />
              <p className="text-sm mt-3">Chưa có ảnh nào</p>
              <button
                onClick={() => setModal({ mode: "add" })}
                className="mt-3 text-xs text-blue-400 hover:text-blue-300 transition-colors"
              >
                + Thêm ảnh đầu tiên
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {items.map((item) => (
                <CarouselCard
                  key={item.id}
                  item={item}
                  onEdit={() => setModal({ mode: "edit", item })}
                  onDelete={() => setDeleteTarget(item)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}